<template>
  <div class="dashboard-page px-2 py-3">
    <section class="dashboard-toolbar mb-3">
      <div class="toolbar-inner">
        <h1 class="dashboard-title mb-0">Dashboard</h1>
        <div class="toolbar-meta">
          <span class="text-muted small"
            >Signed in as
            {{ (userProfileData && userProfileData.username) || "…" }}</span
          >
          <b-button
            size="sm"
            variant="outline-secondary"
            class="ml-2"
            @click="doLogOut"
          >
            Log out
          </b-button>
        </div>
      </div>

      <b-card v-if="canUseViewAs" class="mt-3 view-as-card" body-class="py-2">
        <ViewAsAdminSelect @change="onViewAsChange" />
      </b-card>

      <b-alert
        v-if="viewAsActive"
        show
        variant="warning"
        class="mt-3 mb-0 py-2"
      >
        Simulating what <strong>{{ effectiveDisplayLabel }}</strong> can see (UI
        + data). Your login is unchanged. Open
        <router-link :to="{name: 'KonekoA'}">Koneko A</router-link>
        to compare.
      </b-alert>
    </section>

    <KonekoADashboardWorkload :embedded-view-as-username="konekoViewAsEmbedKey">
      <template
        #default="{
          projectBudgetSnapshotRows,
          visibleMembers,
          calendarMonthKey,
          monthElapsedPercent,
          weekLabel,
          formatHoursCell,
          setProjectSnapshotBudgetBaselineForProject,
          memberHeadlineHours,
          memberHeadlineLimitClause,
          memberHasUnknownProject,
          memberBreakdownMonthTotalLabel,
          memberBreakdownCapTotalLabel,
          budgetStatusTextClass,
          konekoProjectOptions,
          timesheetReassignSavingId,
          canReassignKonekoEntry,
          reassignKonekoTimesheetEntry,
          showKonekoReassignUi,
        }"
      >
        <section
          v-if="effectiveCaps[KONEKO_CAP.dashboardProjectSnapshot]"
          class="mb-3"
        >
          <b-card no-body class="dashboard-section-card">
            <b-card-header
              class="d-flex justify-content-between align-items-center py-2 dashboard-section-header"
            >
              <b-button
                variant="link"
                class="section-toggle text-left p-0 flex-grow-1 text-decoration-none"
                :aria-expanded="snapshotExpanded ? 'true' : 'false'"
                aria-controls="collapse-project-budget-snapshot"
                @click="snapshotExpanded = !snapshotExpanded"
              >
                <span class="section-title">Project Budget Snapshot</span>
                <span class="chevron ml-2" aria-hidden="true">{{
                  snapshotExpanded ? "▼" : "►"
                }}</span>
              </b-button>
            </b-card-header>
            <b-collapse
              id="collapse-project-budget-snapshot"
              v-model="snapshotExpanded"
            >
              <b-card-body class="dashboard-section-inner">
                <KonekoAProjectBudgetSnapshotPanels
                  :rows="projectBudgetSnapshotRows"
                  :calendar-month-key="calendarMonthKey"
                  :timesheet-month-key="timesheetMonthKey"
                  :snapshot-uses-timesheet-fallback="
                    snapshotUsesTimesheetFallback
                  "
                  :is-future-calendar-month="isFutureCalendarMonth"
                  :calendar-month-display-title="calendarMonthDisplayTitle"
                  :month-elapsed-percent="monthElapsedPercent"
                  :week-label="weekLabel"
                  :format-hours-cell="formatHoursCell"
                  :format-signed-hours="formatSignedHours"
                  :on-snapshot-baseline-change="
                    setProjectSnapshotBudgetBaselineForProject
                  "
                  :project-options="konekoProjectOptions"
                  :reassign-saving-id="timesheetReassignSavingId"
                  :can-reassign-entry="canReassignKonekoEntry"
                  :show-reassign-ui="showKonekoReassignUi"
                  @reassign="reassignKonekoTimesheetEntry"
                />
              </b-card-body>
            </b-collapse>
          </b-card>
        </section>

        <section
          v-if="
            effectiveCaps[KONEKO_CAP.dashboardMemberBreakdownAll] ||
            visibleMembers.length
          "
          class="mb-3"
        >
          <b-card no-body class="dashboard-section-card">
            <b-card-header
              class="d-flex justify-content-between align-items-center py-2 dashboard-section-header"
            >
              <b-button
                variant="link"
                class="section-toggle text-left p-0 flex-grow-1 text-decoration-none"
                :aria-expanded="memberExpanded ? 'true' : 'false'"
                aria-controls="collapse-member-breakdown"
                @click="memberExpanded = !memberExpanded"
              >
                <span class="section-title">
                  Member Breakdown ({{ visibleMembers.length }})
                </span>
                <span class="chevron ml-2" aria-hidden="true">{{
                  memberExpanded ? "▼" : "►"
                }}</span>
              </b-button>
            </b-card-header>
            <b-collapse id="collapse-member-breakdown" v-model="memberExpanded">
              <b-card-body class="dashboard-section-inner">
                <div
                  v-if="visibleMembers.length"
                  class="d-flex flex-wrap align-center mb-3"
                  style="gap: 8px"
                >
                  <span class="caption grey--text text--darken-1">Sort by</span>
                  <v-btn-toggle
                    v-model="memberBreakdownSortBy"
                    mandatory
                    dense
                    color="primary"
                    @change="persistMemberBreakdownSortBy"
                  >
                    <v-btn small value="name">Name (default)</v-btn>
                    <v-btn small value="timeSpent">Time spent</v-btn>
                  </v-btn-toggle>
                </div>
                <v-expansion-panels v-if="visibleMembers.length" multiple>
                  <v-expansion-panel
                    v-for="member in visibleMembers"
                    :key="member.username"
                  >
                    <v-expansion-panel-header>
                      <div>
                        <div class="subtitle-2 font-weight-medium">
                          {{ member.name }} at {{ memberHeadlineHours(member) }}
                          {{ memberHeadlineLimitClause(member) }}
                        </div>
                        <div class="caption grey--text text--darken-1">
                          Last logged: {{ member.lastLogged }} · Days since
                          daily report:
                          {{ member.daysSinceDailyReport }}
                        </div>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-simple-table v-if="member.allocations.length" dense>
                        <thead>
                          <tr>
                            <th>Project</th>
                            <th>Month</th>
                            <th>Personal limit</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="line in member.allocations"
                            :key="line.project"
                            :class="budgetStatusTextClass(line.barStatus)"
                          >
                            <td>
                              {{ line.project
                              }}<span
                                v-if="line.project === 'unknown'"
                                class="ml-1"
                                >***</span
                              >
                            </td>
                            <td>{{ formatHoursCell(line.monthLogged) }}</td>
                            <td>
                              <span
                                v-if="line.target"
                                class="d-flex align-center"
                              >
                                <span
                                  class="grey--text text--darken-1 mr-1"
                                  style="font-size: 11px"
                                  >●</span
                                >
                                <span
                                  >{{ line.percent }} of {{ line.target }}</span
                                >
                              </span>
                              <span
                                v-else
                                class="d-flex align-center red--text text--darken-1"
                              >
                                <span class="mr-1" style="font-size: 11px"
                                  >●</span
                                >
                                <span>FLEX</span>
                              </span>
                              <KonekoBudgetRangeBar
                                v-if="line.personalBudgetBar"
                                class="mt-1"
                                :current-decimal="
                                  line.personalBudgetBar.current
                                "
                                :low-decimal="line.personalBudgetBar.low"
                                :high-decimal="line.personalBudgetBar.high"
                                :target-decimal="line.personalBudgetBar.target"
                                :month-elapsed-percent="monthElapsedPercent"
                                :status="line.barStatus"
                              />
                            </td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr
                            class="member-breakdown-totals-row font-weight-medium"
                          >
                            <td>Total</td>
                            <td>
                              {{ memberBreakdownMonthTotalLabel(member) }}
                            </td>
                            <td>{{ memberBreakdownCapTotalLabel(member) }}</td>
                          </tr>
                        </tfoot>
                      </v-simple-table>
                      <div
                        v-if="memberHasUnknownProject(member)"
                        class="caption grey--text mt-2"
                      >
                        *** Hours on unmapped / unknown project IDs.
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
                <v-alert v-else type="info" outlined dense>
                  No member rows for this scope (check filters or backend
                  access).
                </v-alert>
              </b-card-body>
            </b-collapse>
          </b-card>
        </section>
      </template>
    </KonekoADashboardWorkload>
  </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex";
import {canonicalizeKonekoUsername} from "@/config/konekoAConfig";
import {
  STORAGE_COLLAPSE_COMPANY,
  STORAGE_COLLAPSE_PERSONAL,
} from "@/constants/dashboardConstants";
import {
  dashboardViewAsState,
  isKonekoViewAsAdmin,
  syncDashboardViewAsFromStoreGetter,
  viewAsPreviewLabel,
} from "@/utils/dashboardViewAsSync";
import {getKonekoCapabilities, KONEKO_CAP} from "@/config/konekoVisibility";
import ViewAsAdminSelect from "@/components/Layout/ViewAsAdminSelect.vue";
import KonekoADashboardWorkload from "@/components/Dashboard/KonekoADashboardWorkload.vue";
import KonekoBudgetRangeBar from "@/components/Koneko/KonekoBudgetRangeBar.vue";
import KonekoAProjectBudgetSnapshotPanels from "@/components/Koneko/KonekoAProjectBudgetSnapshotPanels.vue";

function readStoredBool(key, defaultValue) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return raw === "1" || raw === "true";
  } catch {
    return defaultValue;
  }
}

function writeStoredBool(key, value) {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export default {
  name: "Dashboard",
  components: {
    ViewAsAdminSelect,
    KonekoADashboardWorkload,
    KonekoBudgetRangeBar,
    KonekoAProjectBudgetSnapshotPanels,
  },
  data() {
    return {
      snapshotExpanded: true,
      memberExpanded: true,
    };
  },
  computed: {
    ...mapGetters(["userProfileData"]),
    KONEKO_CAP() {
      return KONEKO_CAP;
    },
    canUseViewAs() {
      return isKonekoViewAsAdmin(this.userProfileData);
    },
    viewAsActive() {
      return Boolean(dashboardViewAsState.viewingAsPlanningKeyNonSelf);
    },
    konekoViewAsEmbedKey() {
      return dashboardViewAsState.viewingAsPlanningKeyNonSelf || "";
    },
    effectiveDisplayLabel() {
      return viewAsPreviewLabel(
        dashboardViewAsState.viewingAsPlanningKeyNonSelf
      );
    },
    effectiveCaps() {
      void dashboardViewAsState.visibilityOverridesRev;
      const preview = dashboardViewAsState.viewingAsPlanningKeyNonSelf;
      if (preview) return getKonekoCapabilities(preview);
      return getKonekoCapabilities(
        canonicalizeKonekoUsername(
          this.userProfileData && this.userProfileData.username
        )
      );
    },
  },
  watch: {
    userProfileData() {
      syncDashboardViewAsFromStoreGetter(
        this.userProfileData,
        this.$store && this.$store.getters.isAuthenticated
      );
    },
    snapshotExpanded(val) {
      writeStoredBool(STORAGE_COLLAPSE_COMPANY, val);
    },
    memberExpanded(val) {
      writeStoredBool(STORAGE_COLLAPSE_PERSONAL, val);
    },
  },
  created() {
    this.snapshotExpanded = readStoredBool(STORAGE_COLLAPSE_COMPANY, true);
    this.memberExpanded = readStoredBool(STORAGE_COLLAPSE_PERSONAL, true);
    syncDashboardViewAsFromStoreGetter(
      this.userProfileData,
      this.$store && this.$store.getters.isAuthenticated
    );
  },
  methods: {
    ...mapActions(["AUTH_LOGOUT_REQUEST"]),
    onViewAsChange() {
      // KonekoADashboardWorkload reads embeddedViewAsUsername prop; parent re-renders via reactive state.
    },
    doLogOut() {
      this.AUTH_LOGOUT_REQUEST();
      this.$router.push({name: "Login"});
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.toolbar-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.dashboard-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.toolbar-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.view-as-card {
  border-style: dashed;
}

.view-as-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.view-as-label {
  font-weight: 600;
  min-width: 4rem;
}

.view-as-select {
  max-width: 280px;
  min-width: 200px;
}

.dashboard-section-card {
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 4px;
  overflow: hidden;
}

.dashboard-section-header {
  background-color: #f8f9fa;
}

.dashboard-section-inner {
  background-color: #fff;
}

.section-toggle {
  color: #212529 !important;
  font-weight: 600;
}

.section-title {
  font-size: 1.05rem;
}

.chevron {
  font-size: 0.75rem;
  color: #6c757d;
}

.member-breakdown-totals-row {
  background: rgba(0, 0, 0, 0.04);
}

.member-breakdown-totals-row td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
